import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  listWebSessions,
  revokeWebSession,
  type WebSessionRow,
} from "../../services/auth";
import { getUserInfo } from "../../services/me";
import { useTelegram } from "../../hooks";
import { Block, WebBackButton } from "../../components/shared";
import { IS_TELEGRAM } from "../../utils/runtime";

/**
 * `/sessions` — активные веб-сессии юзера. Для TG-версии страница
 * доступна тоже (её можно открыть, если решил зайти на веб с одного
 * устройства и с TG-мобилки хочет проверить), но чаще всего сюда
 * попадают из веба через профиль → «Устройства».
 *
 * Дизайн простой: карточка на сессию, метаданные из user-agent,
 * IP, времена, «текущая» помечается зелёным чипом и revoke на ней
 * блокирован (нельзя рубить сук, на котором сидишь — только logout).
 */
export function SessionsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation("profile");
  const { tg } = useTelegram();
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!IS_TELEGRAM) return;
    const handleBack = () => navigate(-1);
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, tg]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["web-sessions"],
    queryFn: listWebSessions,
    // Обновляем чаще, чем прочие блоки: юзер зашёл сюда именно
    // из подозрения — хочет актуальную картину.
    staleTime: 15_000,
  });

  // Нужно знать, привязан ли Telegram-аккаунт, чтобы решать: рисовать
  // ли виртуальную строку «Telegram Mini App» в списке устройств.
  // `/me` уже кеширован RootLayout'ом, так что этот запрос почти всегда
  // моментальный (из cache).
  const { data: me } = useQuery({
    queryKey: ["user-info"],
    queryFn: getUserInfo,
    staleTime: 60_000,
  });
  const hasTelegram = Boolean(me?.telegram_id);

  const [confirmId, setConfirmId] = useState<string | null>(null);
  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeWebSession(id),
    onSuccess: () => {
      enqueueSnackbar(t("sessions.revoked"), { variant: "success" });
      qc.invalidateQueries({ queryKey: ["web-sessions"] });
    },
    onError: () => {
      enqueueSnackbar(t("sessions.revoke_failed"), { variant: "error" });
    },
    onSettled: () => setConfirmId(null),
  });

  return (
    <Box sx={{ width: "100%", pb: "80px" }}>
      <WebBackButton title={t("sessions.title")} />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, lineHeight: 1.5 }}
      >
        {t("sessions.subtitle")}
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {isError && !isLoading && (
        <Typography variant="body2" color="error.main" sx={{ my: 2 }}>
          {t("common.something_went_wrong", { defaultValue: "Что-то пошло не так" })}
        </Typography>
      )}

      {!isLoading && data && data.length === 0 && !hasTelegram && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">{t("sessions.empty")}</Typography>
        </Box>
      )}

      <Stack spacing={1.5}>
        {/* Виртуальная строка «Telegram Mini App» — рендерим, если
            у юзера есть привязка Telegram-аккаунта. `is_current` только
            когда мы сами в TG (в вебе это просто «есть привязка», не
            текущее устройство). Ревокнуть Telegram-сессию нельзя:
            initData инвалидирована только у Telegram, а не у нас. */}
        {hasTelegram && (
          <TelegramSessionCard
            currentDevice={IS_TELEGRAM}
            username={me?.username ?? null}
          />
        )}

        {(data ?? []).map((s) => (
          <SessionCard
            key={s.id}
            session={s}
            lang={i18n.language}
            onRevoke={() => setConfirmId(s.id)}
          />
        ))}
      </Stack>

      <Dialog
        open={confirmId !== null}
        onClose={() => setConfirmId(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("sessions.revoke_confirm_title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("sessions.revoke_confirm_text")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)} sx={{ textTransform: "none" }}>
            {t("sessions.revoke_cancel")}
          </Button>
          <Button
            onClick={() => confirmId && revokeMut.mutate(confirmId)}
            disabled={revokeMut.isLoading}
            color="error"
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            {t("sessions.revoke_confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------- SessionCard ----------

interface CardProps {
  session: WebSessionRow;
  lang: string;
  onRevoke: () => void;
}

function SessionCard({ session, lang, onRevoke }: CardProps) {
  const { t } = useTranslation("profile");
  const theme = useTheme();

  // Разбираем user-agent на «браузер + ОС» — детальный парсер тут
  // избыточен, просто вытащим самые очевидные бренды.
  const label = describeUserAgent(session.user_agent);
  const icon = iconForUserAgent(session.user_agent);

  const fmt = (iso: string) =>
    new Intl.DateTimeFormat(lang, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));

  return (
    <Block sx={{ padding: "14px 16px" }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(51,119,255,0.14)"
                : "rgba(51,119,255,0.08)",
            color: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon icon={icon} width={22} height={22} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label || t("sessions.unknown_device")}
            </Typography>
            {session.is_current && (
              <Chip
                label={t("sessions.current")}
                size="small"
                color="success"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Stack>
          {session.ip && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontFamily: "monospace" }}
            >
              {session.ip}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {t("sessions.last_used", { date: fmt(session.last_used_at) })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {t("sessions.created", { date: fmt(session.created_at) })}
          </Typography>
        </Box>
        {!session.is_current && (
          <Button
            onClick={onRevoke}
            size="small"
            color="error"
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("sessions.revoke")}
          </Button>
        )}
      </Stack>
    </Block>
  );
}

// ---------- helpers ----------

/** Простой разбор user-agent. Не идеален, но покрывает 95% кейсов. */
function describeUserAgent(ua: string | null): string {
  if (!ua) return "";
  let os = "";
  let browser = "";

  if (/iPhone|iPad/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Windows NT/i.test(ua)) os = "Windows";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome/i.test(ua)) browser = "Chrome";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Safari/i.test(ua)) browser = "Safari";
  else if (/TelegramBot/i.test(ua)) browser = "Telegram";

  return [browser, os].filter(Boolean).join(" · ");
}

// ---------- TelegramSessionCard ----------

/**
 * Виртуальная карточка «Telegram Mini App». Появляется в списке, если
 * у юзера привязан TG-аккаунт (по `me.telegram_id`). Не революка́ется
 * из нашего API: сессия там живёт в связке с initData Telegram-клиента,
 * а не в нашей БД. Единственный способ «выйти» — разлогиниться из
 * Telegram-клиента или разрешить в клиенте отвязку бота.
 */
function TelegramSessionCard(props: {
  currentDevice: boolean;
  username: string | null;
}) {
  const { t } = useTranslation("profile");
  const theme = useTheme();

  const label = props.username
    ? `Telegram · @${props.username}`
    : "Telegram Mini App";

  return (
    <Block sx={{ padding: "14px 16px" }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(46,157,247,0.15)"
                : "rgba(46,157,247,0.10)",
            color: "#2E9DF7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon icon="ic:baseline-telegram" width={22} height={22} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </Typography>
            {props.currentDevice && (
              <Chip
                label={t("sessions.current")}
                size="small"
                color="success"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {t("sessions.telegram_note")}
          </Typography>
        </Box>
      </Stack>
    </Block>
  );
}

function iconForUserAgent(ua: string | null): string {
  if (!ua) return "solar:devices-linear";
  if (/iPhone|Android|Mobile/i.test(ua)) return "solar:smartphone-linear";
  if (/iPad|Tablet/i.test(ua)) return "solar:tablet-linear";
  if (/Mac OS X/i.test(ua)) return "solar:laptop-linear";
  if (/Windows|Linux/i.test(ua)) return "solar:monitor-linear";
  return "solar:devices-linear";
}
